import {Injectable, NgZone} from "@angular/core";
import {MatSnackBar, MatSnackBarRef} from "@angular/material/snack-bar";
import {NotificationComponent} from "./notification.component";
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

// Soort melding (statuskleur + icoon, zie NotificationComponent en de token-groep notification):
// info = ter informatie, success = gelukt, warning = let op / actie nodig, error = mislukt.
export type TNotificationSeverity = 'info' | 'success' | 'warning' | 'error';

export interface INotificationData {
    title?: string;
    text: string;
    // Standaard 'info'; offline-meldingen tonen altijd het cloud_off-icoon.
    severity?: TNotificationSeverity;
    showAction?: boolean;
    duration?: number;
    offline?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService
{
    private __snackBar = injectRequired(MatSnackBar);
    private __zone = injectRequired(NgZone);

    private __messages: INotificationData[] = [];
    private __snackBarRef: MatSnackBarRef<NotificationComponent>;
    private __snackBarIsDisplayed: boolean = false;
    private __outsideClickHandler: ((event: MouseEvent) => void) | null = null;

    public pushNotification(notificationData: INotificationData, duration?: number): void {
        const _duration = notificationData.duration ?? duration ?? 5000;

        notificationData.showAction = _duration == 0;

        // Dezelfde melding staat al in beeld (bv. twee gelijktijdige requests met dezelfde fout):
        // niet nog eens stapelen.
        if (this.__messages.some(m => m.title === notificationData.title && m.text === notificationData.text)) return;

        this.__messages.push(notificationData);

        if (!this.__snackBarIsDisplayed) {
            this.__snackBarIsDisplayed = true;

            this.__zone.run(() => {  // be sure to run in Angular zone
                this.__snackBarRef = this.__snackBar.openFromComponent(NotificationComponent, {
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    // Container zonder eigen (zwarte) achtergrond: elke melding is een eigen gekleurd
                    // kaartje (styles.scss .app-notification-snackbar).
                    panelClass: 'app-notification-snackbar',
                    data: {
                        messages: this.__messages,
                    }
                });
            });

            setTimeout(() => {
                const container = document.querySelector('mat-snack-bar-container');
                this.__outsideClickHandler = (event: MouseEvent) => {
                    if (!container?.contains(event.target as Node)) {
                        this.dismissAll();
                    }
                };

                document.addEventListener('click', this.__outsideClickHandler);
            });

            this.__snackBarRef.afterDismissed().subscribe(() => {
                this.__snackBarIsDisplayed = false;
                if (this.__outsideClickHandler) {
                    document.removeEventListener('click', this.__outsideClickHandler);
                    this.__outsideClickHandler = null;
                }
            });
        }

        // Every message gets its own dismiss timer, whether it opened the
        // snackbar or joined an already-open one - otherwise messages that
        // arrive while a previous one is still showing never auto-dismiss.
        if (_duration) {
            setTimeout(() => this.__snackBarRef?.instance.removeMessage(notificationData), _duration);
        }
    }

    private dismissAll(): void {
        this.__messages.length = 0;
        this.__snackBarRef?.dismiss();
    }
}